import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@artstorefronts/ui';

import { isOnboardingAllowedPath } from '../constants/onboarding';
import { useNeedsOnboarding } from '../hooks/useNeedsOnboarding';

/**
 * The way back into the welcome wizard from the account-management pages a
 * half-onboarded user is allowed to reach (`/settings`, `/billing`, `/plans`).
 *
 * Deliberately not dismissible: `/settings` renders with no `SiteHeader` and no
 * user menu, so dismissing this would trade the old hard lock-in for a silent
 * lock-out. `needsOnboarding` comes from `useNeedsOnboarding` and is never
 * re-derived here — re-deriving it would show a dead "back to setup" link to
 * legacy users whenever the `welcome_flow` flag is off, and would skip the
 * freshly-completed sessionStorage backstop.
 */
export function ResumeOnboardingBanner() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { needsOnboarding } = useNeedsOnboarding();

  if (!needsOnboarding) return null;
  if (!isOnboardingAllowedPath(pathname)) return null;

  return (
    <div className="flex flex-shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900">
      <span>{t('resume-onboarding.title')}</span>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 font-semibold text-amber-900 underline"
        onClick={() => navigate('/welcome')}
      >
        {t('resume-onboarding.cta')}
      </Button>
    </div>
  );
}
